import { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { RiRobot2Fill } from 'react-icons/ri';
import { motion } from 'framer-motion';
import type { ChatMessage as ChatMessageType } from '../types';

interface ChatMessageProps {
  message: ChatMessageType;
}

const ChatMessage = ({ message }: ChatMessageProps) => {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';

  // Format timestamp
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
    >
      <div
        className={`max-w-[80%] rounded-lg px-4 py-2 ${
          isUser
            ? 'bg-theme text-white shadow-md'
            : isAssistant
            ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-2 border-theme/30'
            : 'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
        }`}
      >
        <div className="flex items-start gap-2">
          {/* Message Icon */}
          {isAssistant && (
            <div className="flex-shrink-0 mt-1">
              <RiRobot2Fill className="h-5 w-5 text-theme" />
            </div>
          )}

          {/* Message Content */}
          <div className="flex-1 min-w-0">
            <div className="text-sm">
              {isAssistant ? (
                <div className="prose prose-sm dark:prose-invert max-w-none
                    prose-p:my-2 prose-p:leading-relaxed prose-p:text-gray-900 dark:prose-p:text-gray-100
                    prose-pre:bg-gray-800 prose-pre:text-gray-100 prose-pre:p-3
                    prose-code:text-theme
                    prose-code:bg-gray-100 dark:prose-code:bg-gray-800
                    prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs
                    prose-a:text-theme prose-a:no-underline hover:prose-a:underline
                    prose-strong:text-gray-900 dark:prose-strong:text-gray-100 prose-strong:font-semibold
                    prose-ul:my-2 prose-ol:my-2 prose-ul:list-disc prose-ol:list-decimal
                    prose-li:my-1 prose-li:text-gray-900 dark:prose-li:text-gray-100
                    prose-headings:font-semibold prose-headings:text-gray-900 dark:prose-headings:text-gray-100
                    prose-blockquote:border-l-theme prose-blockquote:italic prose-blockquote:text-gray-700 dark:prose-blockquote:text-gray-300">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {message.message}
                  </ReactMarkdown>
                </div>
              ) : (
                <div className="whitespace-pre-wrap break-words">
                  {message.message}
                </div>
              )}
            </div>
            <div
              className={`text-xs mt-1 ${
                isUser
                  ? 'text-white/80'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {formatTime(message.created_at)}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default memo(ChatMessage);
